import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Flame, Medal, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { avatarSrc } from "../lib/avatars";
import { useAuth } from "../context/AuthContext";
import { JoinButton } from "../components/JoinDialog";

const BOARDS = [
  { id: "grinder", label: "Weekly Grinder", icon: Flame, unit: "pts",
    rules: ["1 point for every hand you're dealt in", "5 more for every hand you win", "Daily streak bonus: 25 points on day 1, +5 each day after", "Weeks run Monday to Sunday"] },
  { id: "bankroll", label: "Monthly Bankroll Builder", icon: Trophy, unit: "chips",
    rules: ["1 point for every chip of profit at cash tables", "Losses take points away", "Prizes paid automatically when the month ends"] },
];
const PLACE = ["text-amber-300", "text-slate-200", "text-orange-300"];

const useCountdown = (endsAt) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(id); }, []);
  const ms = Math.max(0, (endsAt || 0) - now);
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return d ? `${d}d ${h}h` : `${h}h ${m}m`;
};

const Row = ({ r, unit, mine }) => (
  <div data-testid={`lb-row-${r.rank}`} className={`flex items-center gap-4 px-4 py-3 rounded-xl ${mine ? "bg-amber-500/15 border border-amber-400/40" : "bg-black/30 border border-white/5"} lb-row-in`}
    style={{ animationDelay: `${Math.min(r.rank, 20) * 30}ms` }}>
    <div className={`w-8 text-center font-display text-lg ${PLACE[r.rank - 1] || "text-slate-400"}`}>{r.rank <= 3 ? <Medal className="w-5 h-5 mx-auto" /> : r.rank}</div>
    <img src={avatarSrc(r.avatar)} alt="" className="w-9 h-9 rounded-full border border-white/10 bg-slate-800" />
    <div className="flex-1 min-w-0">
      <div className="text-sm text-amber-50 truncate">{r.name}</div>
      <div className="text-[11px] text-slate-500">{r.hands.toLocaleString()} hands · {r.won.toLocaleString()} won</div>
    </div>
    <div className={`font-mono text-base ${r.points < 0 ? "text-red-300" : "text-amber-100"}`}>{r.points.toLocaleString()} <span className="text-[10px] text-slate-500">{unit}</span></div>
  </div>
);

const Board = ({ board, which }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    const load = () => api.get(`/api/leaderboard/${board.id}`, { params: { which } }).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [board.id, which]);
  const left = useCountdown(data?.ends_at);
  if (!data) return <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>;
  const inTop = data.me && data.rows.some((r) => r.user_id === user?.user_id);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 flex flex-col gap-2" data-testid={`lb-board-${board.id}`}>
        <div className="flex items-end justify-between mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] gold-text">{data.label}</div>
            {which === "current" && <div className="text-xs text-slate-400 mt-1" data-testid="lb-ends-in">Ends in {left}</div>}
          </div>
          {data.me && <div data-testid="lb-my-rank" className="text-xs text-slate-300">You: <span className="text-amber-300 font-display">#{data.me.rank}</span> · {data.me.points.toLocaleString()} {board.unit}</div>}
        </div>
        {data.rows.length === 0 && <div data-testid="lb-empty" className="text-sm text-slate-400 py-10 text-center gold-card rounded-xl">No one on the board yet — be the first.</div>}
        {data.rows.map((r) => <Row key={r.user_id} r={r} unit={board.unit} mine={r.user_id === user?.user_id} />)}
        {data.me && !inTop && <><div className="text-center text-slate-600">…</div><Row r={{ ...data.me, user_id: user.user_id, name: user.display_name, avatar: user.avatar }} unit={board.unit} mine /></>}
      </div>
      <aside className="lg:col-span-4 flex flex-col gap-4">
        {data.guest && (
          <div data-testid="lb-guest-join" className="gold-card rounded-2xl p-5 flex flex-col gap-3">
            <div className="font-display text-sm text-amber-50 tracking-wider">GET ON THE BOARD</div>
            <p className="text-xs text-slate-300">Leaderboards are for members. Join free for 50,000 chips — and every hand from then on counts.</p>
            <JoinButton testId="lb-join-button" label="JOIN · 50,000 CHIPS" reason="Leaderboards are for members." />
          </div>
        )}
        <div className="gold-card rounded-2xl p-5" data-testid="lb-rules">
          <div className="text-[11px] uppercase tracking-[0.3em] gold-text mb-3">How points work</div>
          <ul className="flex flex-col gap-2 text-xs text-slate-300">{board.rules.map((t) => <li key={t}>· {t}</li>)}</ul>
        </div>
        {data.prizes_start && (
          <div className="gold-card rounded-2xl p-5" data-testid="lb-prizes-soon">
            <div className="text-[11px] uppercase tracking-[0.3em] gold-text mb-2 flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> Prizes start {data.prizes_start}</div>
            <p className="text-xs text-slate-300">From {data.prizes_start}: 1st 50,000 chips + a Champion badge, 2nd and 3rd 20,000 chips — paid automatically when the month ends.</p>
          </div>
        )}
        {data.prizes?.length > 0 && (
          <div className="gold-card rounded-2xl p-5" data-testid="lb-prizes">
            <div className="text-[11px] uppercase tracking-[0.3em] gold-text mb-3 flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> Monthly prizes</div>
            <div className="flex flex-col gap-1.5 text-sm">
              {data.prizes.map((c, i) => <div key={i} className="flex justify-between"><span className={PLACE[i]}>{["1st", "2nd", "3rd"][i]}</span><span className="font-mono text-amber-100">{c.toLocaleString()} chips{i === 0 ? " + Champion badge" : ""}</span></div>)}
            </div>
            <p className="text-[10px] text-slate-500 mt-3">Play chips only — no cash value.</p>
          </div>
        )}
        {data.last_winners?.length > 0 && (
          <div className="gold-card rounded-2xl p-5" data-testid="lb-last-winners">
            <div className="text-[11px] uppercase tracking-[0.3em] gold-text mb-3">Last month's winners</div>
            {data.last_winners.map((w) => <div key={w.rank} className="flex justify-between text-sm"><span className={PLACE[w.rank - 1]}>{w.rank}. {w.name}</span><span className="font-mono text-slate-300">{w.points.toLocaleString()}</span></div>)}
          </div>
        )}
      </aside>
    </div>
  );
};

export default function LeaderboardPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("grinder");
  const [which, setWhich] = useState("current");
  const board = BOARDS.find((b) => b.id === tab);
  return (
    <div className="min-h-screen px-8 py-8 md:px-16 bg-slate-950 noise" data-testid="leaderboard-page">
      <button data-testid="lb-back" onClick={() => nav("/lobby")} className="text-xs text-slate-400 hover:text-amber-300 flex items-center gap-1 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Lobby</button>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-2">Leaderboard</h1>
      <p className="text-sm md:text-base text-slate-400 mb-8">Members only · Brisbane time · weeks start Monday</p>
      <div className="flex flex-wrap items-center gap-3 mb-8">
        {BOARDS.map((b) => (
          <button key={b.id} data-testid={`lb-tab-${b.id}`} onClick={() => { setTab(b.id); setWhich("current"); }}
            className={`rounded-full px-5 py-2.5 text-xs font-display tracking-[0.15em] flex items-center gap-2 btn-lift ${tab === b.id ? "btn-gold" : "gold-card text-slate-300 hover:text-white"}`}>
            <b.icon className="w-3.5 h-3.5" /> {b.label.toUpperCase()}
          </button>
        ))}
        <div className="ml-auto flex gap-1 bg-black/40 rounded-full p-1">
          {[["current", tab === "grinder" ? "This week" : "This month"], ["previous", tab === "grinder" ? "Last week" : "Last month"]].map(([k, l]) => (
            <button key={k} data-testid={`lb-period-${k}`} onClick={() => setWhich(k)} className={`rounded-full px-3 py-1.5 text-xs ${which === k ? "bg-amber-500 text-slate-900" : "text-slate-300 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Board board={board} which={which} />
    </div>
  );
}
